'use client';
import { useLocale } from 'use-intl';
import { getCMSIntlVal, tCMSLineBreak } from '../utils/common';
import { ForumItem, MeetingItem } from '../type';
import SessionInfoList from './SessionInfoList';
import SessionTabList from './SessionTabList';
import Tabs, { TabPane, TabsRef } from './TabCarousel';
import TabTitle from './TabTitle';
import { useEffect, useMemo, useRef, useState } from 'react';
import customEvents from '../utils/customEvents';
// import { Button, DialogPlugin, Image } from 'tdesign-react';
import { useMedia } from '@ssv-website/hooks';
// import { useMeetingStatus } from '../context/MeetingConfigContext';

const SessionInfoItem = ({
  className = '',
  item,
}: {
  className?: string;
  item: MeetingItem;
}) => {
  const { speaker } = item || {};
  const locale = useLocale();
  const title = tCMSLineBreak(getCMSIntlVal(item, 'title', locale));

  return (
    <div className={`flex flex-col gap-2 py-1 ${className}`}>
      <h6 className="text-sm font-400 break-words whitespace-pre-wrap">
        {title}
      </h6>
      {speaker.map((speakerItem) => {
        const title = getCMSIntlVal(speakerItem, 'title', locale);
        const name = getCMSIntlVal(speakerItem, 'name', locale);
        const titles: string[] = title.split(';') || [];
        return (
          <div key={speakerItem.id}>
            <p className="text-xs break-words mb-[2px]">{name}</p>
            {titles.map((speakerTitle, index) => {
              return (
                <p
                  key={index}
                  className="text-[11px] leading-[1.5] text-[#121C59]/60 font-[290] break-words"
                >
                  {speakerTitle}
                </p>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

// const MeetingLiveComponet = ({ forumItem }: { forumItem: ForumItem }) => {
//   const { liveQrCode = '', liveUrl = '' } = forumItem;
//   const t = useTranslations('s2025');
//   const isMobile = useMedia('mobile');
//   const status = useMeetingStatus();

//   // 预约直播
//   const handleBookingLiveVideo = () => {
//     if (isMobile) {
//       window.open(liveUrl, '_blank');
//     } else {
//       const confirmDia = DialogPlugin.confirm({
//         body: (
//           <div className="flex flex-col justify-center items-center px-10">
//             <Image
//               src={liveQrCode}
//               className="w-[252px] h-[399px] !bg-transparent"
//             ></Image>
//           </div>
//         ),
//         footer: false,
//         confirmBtn: null,
//         cancelBtn: null,
//         width: 'auto',
//         onConfirm: ({ e }) => {
//           confirmDia.hide();
//         },
//         onClose: ({ e, trigger }) => {
//           confirmDia.hide();
//         },
//       });
//     }
//   };

//   // 观看直播
//   const handleSeeLiveVideo = () => {
//     alert('前往观看直播' + liveUrl);
//   };
//   return (
//     <div className="mt-12 mobile:mt-6 flex">
//       {/* 未开始 */}
//       {status === MeetingStatus.NotStarted && (
//         <Button
//           className="!w-[165px] !h-[64px] !leading-[64px] !text-[24px] !border-[#1578FF] !rounded-none !bg-white !text-[#1578FF] mobile:!w-full mobile:!h-[45px] mobile:!text-sm"
//           onClick={handleBookingLiveVideo}
//         >
//           {t('预约直播')}
//         </Button>
//       )}
//       {/* 进行中 */}
//       {status === MeetingStatus.InProgress && (
//         <Button
//           className="!w-[165px] !h-[64px] !leading-[64px] !text-[24px] !font-300 !rounded-none !bg-[#1C3FF0] hover:!bg-[#2374E7] mobile:!w-full mobile:!h-[45px] mobile:!text-xs"
//           style={{
//             background:
//               'url(https://cdn-digital.ssv.qq.com/upload//s2025/register-now.png) no-repeat center/cover',
//             border: 'none',
//           }}
//           onClick={() => {
//             handleSeeLiveVideo();
//           }}
//         >
//           {t('观看直播')}
//         </Button>
//       )}
//     </div>
//   );
// };

const RenderTabPaneContent = ({
  forumItem,
  onChangeTab,
}: {
  forumItem: ForumItem;
  onChangeTab?: (id: string) => void;
}) => {
  const locale = useLocale();
  const { meetingParagraphList = [] } = forumItem;
  const [activeId, setActiveId] = useState(meetingParagraphList[0]?.id || '');

  useEffect(() => {
    onChangeTab?.(activeId);
  }, [activeId, onChangeTab]);

  const tabList = useMemo(() => {
    return meetingParagraphList.map((paragraphItem) => {
      const title = tCMSLineBreak(
        getCMSIntlVal(paragraphItem, 'title', locale) as string
      );
      return {
        id: paragraphItem.id,
        timeText: paragraphItem.timeText,
        title: title,
      };
    });
  }, [locale, meetingParagraphList]);

  const meetingList = useMemo(() => {
    const item =
      meetingParagraphList.find((item) => item.id === activeId) || null;
    return item?.meetingList || [];
  }, [activeId, meetingParagraphList]);

  return (
    <>
      {/* <MeetingLiveComponet forumItem={forumItem}></MeetingLiveComponet> */}
      {/* PC */}
      <div className={`flex gap-[133px] mt-12 mobile:hidden`}>
        <SessionTabList
          list={tabList}
          onChange={({ id }) => {
            setActiveId(id);
          }}
        />
        <SessionInfoList key={activeId} list={meetingList || []} />
      </div>

      {/* mobile */}
      <div className={`relative text-[#121C59] mt-6 pr-5 hidden mobile:block`}>
        <span className="absolute top-[12px] bottom-[12px] left-[3.5px] w-px bg-[#C1C8DE]"></span>
        {meetingParagraphList.map((paragraphItem) => {
          const { timeText, meetingList } = paragraphItem || {};
          const title = tCMSLineBreak(
            getCMSIntlVal(paragraphItem, 'title', locale) as string
          );
          return (
            <div className="mt-4" key={paragraphItem.id}>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-[#0A64E4] mr-4 relative z-10 flex-shrink-0"></div>
                <h5 className="relative text-lg font-400 mr-4 break-words min-w-0">
                  {title}
                  <i
                    className="absolute h-[6px] bottom-0 left-0 right-0 w-full"
                    style={{
                      background:
                        'linear-gradient(90deg, rgba(28, 63, 240, 0.70) 0.01%, rgba(28, 63, 240, 0.21) 71.34%, rgba(21, 34, 99, 0.00) 99.99%)',
                    }}
                  ></i>
                </h5>
                <div className="text-sm font-[300] flex-shrink-0 font-tcn1">
                  {timeText}
                </div>
              </div>
              <div className="pl-6">
                {meetingList.map((meetingItem) => (
                  <SessionInfoItem
                    key={meetingItem.id}
                    item={meetingItem}
                    className="mt-3"
                  ></SessionInfoItem>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

interface SessionInfoContainerProps {
  forumList: ForumItem[];
  onSubForumChange?: () => void;
}
const SessionInfo = ({ forumList }: SessionInfoContainerProps) => {
  // 过滤会议无需展示的tab
  forumList = forumList.filter((item) => !Number(item.isMtListHidden));
  const defaultTabKey = forumList[0].id;
  const tabRef = useRef<TabsRef>(null);
  const isMobile = useMedia('mobile');
  const locale = useLocale();
  const handleSwitchForumTab = (data: { id?: string }) => {
    tabRef.current?.onSwitchTab(data.id || forumList[0]?.id);
  };
  useEffect(() => {
    customEvents.on('onSwitchForumTab', handleSwitchForumTab);
    return () => {
      customEvents.off('onSwitchForumTab', handleSwitchForumTab);
    };
  }, []);
  return (
    <Tabs
      activeKey={defaultTabKey}
      ref={tabRef}
      openSticky={isMobile}
      stickyOffset={60}
      titleClassName="mobile:top-12 bg-[#EDF3FF]"
      titleScrollClassName="mobile:ml-5"
      titleContentClassName="mobile:mr-5"
      paneScrollClassName="mobile:px-5"
      disableDrag={!isMobile}
    >
      {forumList.map((item) => {
        const name = getCMSIntlVal(item, 'name', locale);
        const desc = getCMSIntlVal(item, 'desc', locale);
        return (
          <TabPane
            key={item.id}
            tabKey={item.id}
            title={({ isActive }) => {
              return (
                <TabTitle title={name} subTitle={desc} isActive={isActive} />
              );
            }}
          >
            <RenderTabPaneContent
              onChangeTab={() => tabRef.current?.adjustTabHeight()}
              forumItem={item}
            />
          </TabPane>
        );
      })}
    </Tabs>
  );
};

export default SessionInfo;
